import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Crown, CircleAlert, Loader } from "lucide-react";

export default function PaymentSuccess() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | timeout | error
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setStatus("paid");
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
      } catch (e) { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="bg-card border-2 border-border p-10 max-w-md w-full text-center block-shadow">
        {status === "checking" && (
          <>
            <Loader className="animate-spin text-primary mx-auto mb-4" size={40} />
            <h1 className="font-display text-2xl font-bold mb-2">Confirming payment...</h1>
            <p className="text-sm text-muted-foreground">Hang tight, unlocking your access.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <Crown className="text-accent mx-auto mb-4" size={48} />
            <h1 className="font-display text-3xl font-bold mb-2">YOU'RE IN.</h1>
            <p className="text-sm text-muted-foreground mb-6">Premium unlocked. Full-length listening, no cutoffs.</p>
            <Button data-testid="success-continue" onClick={() => nav("/")} className="w-full h-12 bg-primary hover:bg-white hover:text-primary rounded-none font-bold overline">Start Listening</Button>
          </>
        )}
        {(status === "timeout" || status === "error") && (
          <>
            <CircleAlert className="text-primary mx-auto mb-4" size={44} />
            <h1 className="font-display text-2xl font-bold mb-2">{status === "timeout" ? "Still processing" : "Something went wrong"}</h1>
            <p className="text-sm text-muted-foreground mb-6">{status === "timeout" ? "Your payment may still be confirming. Check back shortly." : "We couldn't confirm the payment."}</p>
            <Button data-testid="success-retry" onClick={() => nav("/subscribe")} variant="outline" className="w-full h-12 rounded-none border-2 font-bold overline">Back to Subscribe</Button>
          </>
        )}
      </div>
    </div>
  );
}
